import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { resetPassword } from "../actions/authenActions";
import { Box, Button, TextField, Typography, Alert } from "@mui/material";
import { useNavigate, useLocation, Link } from "react-router-dom";
import Layout from "./Layout";

const ResetPassword = () => {
  const location = useLocation();

  const [formData, setFormData] = useState({
    user_id: "",
    license_key: "",
    otp: "",
    password: "",
    confirmPassword: "",
    machine_uuid: "00000000-0000-0000-0000-000000000000",
    deployment_type: "web",
  });

  const [formErrors, setFormErrors] = useState({});
  const [submissionError, setSubmissionError] = useState(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const passwordReset = useSelector((state) => state.authen.passwordReset);

  useEffect(() => {
    if (passwordReset) {
      if (passwordReset.status === "Pass") {
        navigate("/login", {
          state: { message: "Your password has been reset. Please log in." },
        });
      } else {
        setSubmissionError(passwordReset.status || "Password reset failed");
      }
    }
  }, [passwordReset, navigate]);

  const validate = () => {
    const errors = {};

    if (!formData.user_id) {
      errors.user_id = "Username is required";
    }

    if (!formData.license_key) {
      errors.license_key = "License key is required";
    }

    if (!formData.otp) {
      errors.otp = "OTP is required";
    } else if (!/^\d{6}$/.test(formData.otp)) {
      errors.otp = "Enter the 6-digit code from your email";
    }

    if (!formData.password) {
      errors.password = "Password is required";
    } else {
      if (formData.password.length < 12) {
        errors.password = "Password must be at least 12 characters";
      } else if (formData.password.length > 24) {
        errors.password = "Password must be less than 24 characters";
      } else if (!/(?=.*[a-z])/.test(formData.password)) {
        errors.password = "Password must contain at least one lowercase letter";
      } else if (!/(?=.*[A-Z])/.test(formData.password)) {
        errors.password = "Password must contain at least one uppercase letter";
      } else if (!/(?=.*\d)/.test(formData.password)) {
        errors.password = "Password must contain at least one digit";
      } else if (
        !/(?=.*[[\]!@#$%^&*()_\-+=\]{};':"\\|,.<>/?])/.test(formData.password)
      ) {
        errors.password =
          "Password must contain at least one special character";
      }
    }

    if (!formData.confirmPassword) {
      errors.confirmPassword = "Please confirm your password";
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }

    return errors;
  };

  function handleChange(e) {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setFormErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
    setSubmissionError(null);
  }

  function handleSubmit(e) {
    e.preventDefault();

    const errors = validate();
    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    dispatch(resetPassword(formData))
      .then(() => {})
      .catch((error) => {
        setSubmissionError(error?.message || "An unexpected error occurred");
      });
  }

  return (
    <Layout>
      <Typography variant="h5">Reset your password</Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
        Enter the username, license key and one-time code from your email, along
        with a new password.
      </Typography>

      <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
        {submissionError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {submissionError}
          </Alert>
        )}

        <TextField
          name="user_id"
          label="Username"
          fullWidth
          margin="normal"
          value={formData.user_id}
          onChange={handleChange}
          error={!!formErrors.user_id}
          helperText={formErrors.user_id}
        />

        <TextField
          name="license_key"
          label="License Text"
          multiline
          rows={4}
          fullWidth
          margin="normal"
          value={formData.license_key}
          onChange={handleChange}
          error={!!formErrors.license_key}
          helperText={formErrors.license_key}
        />

        <TextField
          name="otp"
          label="One-Time Code (OTP)"
          fullWidth
          margin="normal"
          value={formData.otp}
          onChange={handleChange}
          error={!!formErrors.otp}
          helperText={formErrors.otp || "Check your email for the 6-digit code"}
          inputProps={{ maxLength: 6, pattern: "[0-9]*" }}
        />

        <TextField
          name="password"
          label="New Password"
          type="password"
          fullWidth
          margin="normal"
          value={formData.password}
          onChange={handleChange}
          error={!!formErrors.password}
          helperText={formErrors.password}
        />

        <TextField
          name="confirmPassword"
          label="Reconfirm New Password"
          type="password"
          fullWidth
          margin="normal"
          value={formData.confirmPassword}
          onChange={handleChange}
          error={!!formErrors.confirmPassword}
          helperText={formErrors.confirmPassword}
        />

        <Button fullWidth variant="contained" type="submit" sx={{ mt: 2 }}>
          Reset Password
        </Button>

        <Box mt={2}>
          <Link to="/login">Back to Log In</Link>
        </Box>
      </Box>
    </Layout>
  );
};

export default ResetPassword;
